import React, { useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../config/API";

function UserPayPendingAmount() {
  const location = useLocation();
  const navigate = useNavigate();

  const user = location.state;

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================================================
  // IF NO DATA
  // =========================================================

  if (!user) {
    return (
      <div style={styles.emptyPage}>
        <div style={styles.emptyCard}>
          <div style={styles.emptyIcon}>💳</div>

          <h2>No Payment Details Found</h2>

          <p>Please select a pending payment from the payment details page.</p>

          <button
            style={styles.backButton}
            onClick={() => navigate("/userPaymentDetails")}
          >
            ← Back to Payment Details
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // AMOUNTS
  // =========================================================

  const bedCost = Number(user.userBedCost || 0);
  const alreadyPaid = Number(user.userPaidAmount || 0);

  const pendingAmount =
    Number(user.pendingAmount) || Math.max(bedCost - alreadyPaid, 0);

  const enteredAmount = Number(paymentAmount || 0);

  const remainingAmount = Math.max(pendingAmount - enteredAmount, 0);

  // =========================================================
  // PAYMENT VALIDATION
  // =========================================================

  const validatePayment = () => {
    if (!paymentAmount || enteredAmount <= 0) {
      alert("Please enter a valid payment amount.");
      return false;
    }

    if (enteredAmount > pendingAmount) {
      alert(
        `Payment amount cannot be greater than pending amount ₹${pendingAmount.toLocaleString(
          "en-IN",
        )}.`,
      );
      return false;
    }

    if (!paymentMode) {
      alert("Please select payment mode.");
      return false;
    }

    return true;
  };

  // =========================================================
  // GET JWT
  // =========================================================

  const getAuthHeaders = () => {
    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));

      if (!loggedUser?.jwtToken) {
        return null;
      }

      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${loggedUser.jwtToken}`,
      };
    } catch (error) {
      console.error("Unable to read logged user:", error);
      return null;
    }
  };

  // =========================================================
  // OFFLINE PAYMENT POST
  // =========================================================

  const handleOfflinePayment = async () => {
    if (!validatePayment()) {
      return;
    }

    try {
      setLoading(true);

      const headers = getAuthHeaders();

      if (!headers) {
        alert("Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      const loggedUser = JSON.parse(localStorage.getItem("user"));

      // =====================================================
      // PAYMENT PAYLOAD
      // =====================================================

      const payload = {
        userRegisterId: user.userRegisterId,

        bedId: user.bedId,

        userBookingId: user.userBookingId,

        userBedCost: bedCost,

        userPaidAmount: alreadyPaid,

        pendingAmount: pendingAmount,

        paymentAmount: enteredAmount,

        remainingAmount: remainingAmount,

        paymentMode: paymentMode,

        paymentType: user.paymentType || "MONTH",

        paymentStatus: remainingAmount === 0 ? "SUCCESS" : "PENDING",

        remarks: remarks,

        paymentUpdatedBy: loggedUser?.emailId || "",
      };

      console.log("Offline Payment Payload:", payload);

      // =====================================================
      // POST API
      // =====================================================

      const response = await axios.post(
        `${API.V2}/payment/pendingPaymentByOffline`,
        payload,
        {
          headers,
        },
      );

      console.log("Offline Payment Response:", response.data);

      // =====================================================
      // SUCCESS
      // =====================================================

      if (response.data?.statusCode === 200 || response.status === 200) {
        alert(
          remainingAmount === 0
            ? "Payment completed successfully."
            : `Payment recorded successfully. ₹${remainingAmount.toLocaleString(
                "en-IN",
              )} is still pending.`,
        );

        // Go back to User Payment Details page
        navigate("/showUserPaymentDetails");
      } else {
        alert(response.data?.message || "Unable to record offline payment.");
      }
    } catch (error) {
      console.error("Offline payment error:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        alert("Session expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
      } else {
        alert(
          error.response?.data?.message ||
            "Something went wrong while recording payment.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // PAY FULL PENDING AMOUNT
  // =========================================================

  const handlePayFullAmount = () => {
    setPaymentAmount(String(pendingAmount));
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div style={styles.page}>
      {/* HEADER */}

      <div style={styles.headerCard}>
        <div>
          <h1 style={styles.title}>💰 Pay Pending Amount</h1>

          <p style={styles.subtitle}>Record offline payment for the user</p>
        </div>

        <button style={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      {/* USER DETAILS */}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>👤 User Details</h2>

        <div style={styles.detailsGrid}>
          <Detail label="User Register ID" value={user.userRegisterId} />

          <Detail label="Full Name" value={user.fullName} />

          <Detail label="Email" value={user.emailId} />

          <Detail label="Contact Number" value={user.contactNo} />

          <Detail label="Bed Number" value={user.bedNumber} />
        </div>
      </div>

      {/* PAYMENT SUMMARY */}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>💳 Payment Summary</h2>

        <div style={styles.amountGrid}>
          <AmountCard
            title="Total Bed Cost"
            amount={bedCost}
            className="total"
          />

          <AmountCard
            title="Already Paid"
            amount={alreadyPaid}
            className="paid"
          />

          <AmountCard
            title="Pending Amount"
            amount={pendingAmount}
            className="pending"
          />
        </div>
      </div>

      {/* PAYMENT FORM */}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>💵 Payment Details</h2>

        <div style={styles.formGrid}>
          {/* PAYMENT MODE */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Payment Mode</label>

            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              style={styles.input}
            >
              <option value="CASH">Cash</option>

              <option value="OFFLINE">Offline</option>
            </select>
          </div>

          {/* PAYMENT AMOUNT */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Payment Amount</label>

            <div style={styles.amountInputContainer}>
              <span style={styles.rupee}>₹</span>

              <input
                type="number"
                min="1"
                max={pendingAmount}
                value={paymentAmount}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "" || Number(value) <= pendingAmount) {
                    setPaymentAmount(value);
                  }
                }}
                placeholder="Enter amount"
                style={styles.amountInput}
              />
            </div>

            <small style={styles.helpText}>
              Maximum payable amount: ₹{pendingAmount.toLocaleString("en-IN")}
            </small>
          </div>
        </div>

        {/* PAY FULL */}

        <button
          style={styles.fullAmountButton}
          onClick={handlePayFullAmount}
          disabled={pendingAmount <= 0}
        >
          Pay Full Pending Amount ₹{pendingAmount.toLocaleString("en-IN")}
        </button>

        {/* REMARKS */}

        <div style={styles.formGroup}>
          <label style={styles.label}>Remarks</label>

          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter payment remarks..."
            rows="4"
            style={styles.textarea}
          />
        </div>

        {/* PAYMENT PREVIEW */}

        <div style={styles.previewBox}>
          <div style={styles.previewRow}>
            <span>Pending Before Payment</span>

            <strong>₹{pendingAmount.toLocaleString("en-IN")}</strong>
          </div>

          <div style={styles.previewRow}>
            <span>Payment Amount</span>

            <strong style={{ color: "#2563eb" }}>
              ₹{enteredAmount.toLocaleString("en-IN")}
            </strong>
          </div>

          <div style={styles.divider}></div>

          <div style={styles.previewRow}>
            <span>Remaining Pending</span>

            <strong
              style={{
                color: remainingAmount > 0 ? "#dc2626" : "#16a34a",
              }}
            >
              ₹{remainingAmount.toLocaleString("en-IN")}
            </strong>
          </div>
        </div>

        {/* CONFIRM BUTTON */}

        <button
          style={{
            ...styles.confirmButton,
            opacity: loading ? 0.6 : 1,
          }}
          onClick={handleOfflinePayment}
          disabled={loading || pendingAmount <= 0}
        >
          {loading ? "Processing Payment..." : "✓ Confirm Offline Payment"}
        </button>
      </div>
    </div>
  );
}

// =========================================================
// DETAIL COMPONENT
// =========================================================

function Detail({ label, value }) {
  return (
    <div style={styles.detailBox}>
      <span style={styles.detailLabel}>{label}</span>

      <strong style={styles.detailValue}>{value || "-"}</strong>
    </div>
  );
}

// =========================================================
// AMOUNT COMPONENT
// =========================================================

function AmountCard({ title, amount, className }) {
  let amountColor = "#334155";

  if (className === "paid") {
    amountColor = "#16a34a";
  }

  if (className === "pending") {
    amountColor = "#dc2626";
  }

  return (
    <div style={styles.amountCard}>
      <span style={styles.amountTitle}>{title}</span>

      <strong
        style={{
          ...styles.amountValue,
          color: amountColor,
        }}
      >
        ₹{amount.toLocaleString("en-IN")}
      </strong>
    </div>
  );
}

export default UserPayPendingAmount;

// =========================================================
// STYLES
// =========================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#eff6ff,#f8fafc,#ffffff)",
    padding: "30px",
  },

  headerCard: {
    background: "#ffffff",
    borderRadius: "18px",
    padding: "25px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    color: "#1e3a8a",
    fontSize: "28px",
    fontWeight: "800",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
  },

  backButton: {
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    padding: "11px 20px",
    borderRadius: "10px",
    fontWeight: "700",
    cursor: "pointer",
  },

  card: {
    background: "#ffffff",
    borderRadius: "18px",
    padding: "25px",
    marginBottom: "20px",
    boxShadow: "0 10px 30px rgba(0,0,0,.07)",
  },

  sectionTitle: {
    marginTop: 0,
    marginBottom: "20px",
    color: "#1e293b",
    fontSize: "20px",
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: "15px",
  },

  detailBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  detailLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "600",
  },

  detailValue: {
    color: "#1e293b",
    fontSize: "15px",
  },

  amountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: "15px",
  },

  amountCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "22px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  amountTitle: {
    color: "#64748b",
    fontSize: "14px",
    fontWeight: "600",
  },

  amountValue: {
    fontSize: "25px",
    fontWeight: "800",
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
    gap: "20px",
    marginBottom: "20px",
  },

  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "20px",
  },

  label: {
    color: "#334155",
    fontWeight: "700",
    fontSize: "14px",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "15px",
    background: "#ffffff",
  },

  amountInputContainer: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#ffffff",
  },

  rupee: {
    padding: "0 14px",
    fontSize: "18px",
    fontWeight: "700",
    color: "#475569",
  },

  amountInput: {
    flex: 1,
    border: "none",
    outline: "none",
    padding: "13px 10px",
    fontSize: "16px",
  },

  helpText: {
    color: "#64748b",
    fontSize: "12px",
  },

  fullAmountButton: {
    border: "1px solid #2563eb",
    background: "#eff6ff",
    color: "#2563eb",
    padding: "11px 16px",
    borderRadius: "9px",
    fontWeight: "700",
    cursor: "pointer",
    marginBottom: "20px",
  },

  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    padding: "12px",
    resize: "vertical",
    fontSize: "14px",
    outline: "none",
  },

  previewBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "20px",
  },

  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    color: "#475569",
  },

  divider: {
    borderTop: "1px solid #e2e8f0",
    margin: "8px 0",
  },

  confirmButton: {
    width: "100%",
    border: "none",
    background: "#16a34a",
    color: "#ffffff",
    padding: "15px",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
  },

  emptyPage: {
    minHeight: "100vh",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },

  emptyCard: {
    background: "#ffffff",
    padding: "40px",
    borderRadius: "18px",
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
    maxWidth: "500px",
  },

  emptyIcon: {
    fontSize: "50px",
    marginBottom: "10px",
  },
};
